// Quick-add sheet for the shopping list.
//
// "corn, apples, beer" should land three rows, not one — the placeholder
// has been promising batch-add since v1 but the original implementation
// only added a single row using the entire string as the name. The
// rebuild splits on commas + newlines so a single keystroke run can
// jot down a whole list. ENTER also commits.
//
// Visual: MCM cream glass sheet that matches the rest of the app.
// Forma for labels + buttons, New Kansas italic for the actual item
// names being typed (consistent with item-title surfaces). Burnt-
// orange CTA gradient on ADD to read as a primary action against
// the cream sheet. emoji is "" — never null, never omitted:
// shopping_list_items.emoji is NOT NULL (unlike pantry_items
// post-0147), so an explicit null dies with a not-null-violation
// banner, and OMITTING the key lets the column default stamp
// '🥫' onto the row — which every family member's list then
// renders, violating the emoji-free rule. Empty string satisfies
// the constraint and renders nothing.
//
// Mount pattern: parent wraps in <AnimatePresence> so the slide-down
// exit fires before the conditional render unmounts the component.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";

export default function ShoppingQuickAdd({ onClose, onAdd }) {
  const [text, setText] = useState("");
  const [qty, setQty]   = useState(1);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Parse the input as a list — commas and newlines both split. Empty
  // tokens are dropped, each token is trimmed. Memoized so the live
  // count badge in the ADD button doesn't recompute per render.
  const parsed = useMemo(() => {
    return text
      .split(/[,\n]+/g)
      .map(s => s.trim())
      .filter(Boolean);
  }, [text]);
  const canAdd = parsed.length > 0;

  function newId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function submit() {
    if (!canAdd) return;
    // Each token gets its own row. qty applies UNIFORMLY — adding
    // "corn, apples, beer" with qty=2 lands 2 of each.
    for (const name of parsed) {
      onAdd?.({
        id: newId(),
        name,
        emoji: "",
        amount: qty,
        unit: "",
        category: "pantry",
        source: "manual",
        ingredientId: null,
        priceCents: null,
      });
    }
    onClose?.();
  }

  function handleKey(e) {
    // Cmd/Ctrl+Enter and plain Enter both submit. Plain Enter without a
    // modifier is the more common path on iOS keyboards (no Cmd).
    if (e.key === "Enter") {
      // If the user is mid-list and just hit Enter to start a new line,
      // honor that — the textarea naturally creates one. We only commit
      // when there's content AND the keystroke wasn't a Shift+Enter
      // (which inserts a newline). Submit on plain Enter to match the
      // single-shot use case.
      if (!e.shiftKey) {
        e.preventDefault();
        submit();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  }

  return (
    <motion.div
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.18 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      style={{
        position: "fixed", inset: 0, zIndex: 160,
        // Glass-tinted backdrop — softer than the old rgba(0,0,0,0.7),
        // matches the rest of the cream / glass UI.
        background: "rgba(30,20,8,0.32)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1, transition: { type: "spring", stiffness: 360, damping: 32 } }}
        exit={{ y: 60, opacity: 0, transition: { duration: 0.18, ease: [0.4, 0, 0.2, 1] } }}
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 520,
          background: COLOR.night,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderTop: `1px solid ${COLOR.border}`,
          padding: "20px 22px calc(28px + env(safe-area-inset-bottom))",
          boxShadow: "0 -16px 48px rgba(30,20,8,0.18), 0 -2px 6px rgba(30,20,8,0.06)",
        }}
      >
        {/* Drag handle pill */}
        <div style={{
          width: 38, height: 4, borderRadius: 2,
          background: COLOR.border,
          margin: "0 auto 14px",
        }} />

        {/* Kicker + close */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 10,
        }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
            color: COLOR.gold, letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}>
            Add to Shopping List
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30,
              background: "transparent",
              border: `1px solid ${COLOR.border}`,
              color: COLOR.muted,
              borderRadius: 15,
              fontFamily: FONT.forma, fontSize: 14, fontWeight: 500,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Section heading — form-context italic Forma 600 (the
            AddDraftSheet pattern). New Kansas is reserved for item
            display; Pale Martini is reserved for page-level noun-
            phrase titles, never questions. */}
        <div style={{
          fontFamily: FONT.forma,
          fontStyle: "italic",
          fontWeight: 600,
          fontSize: 22,
          lineHeight: 1.2,
          color: COLOR.ink,
          marginBottom: 4,
        }}>
          What needs picking up?
        </div>
        <div style={{
          fontFamily: FONT.forma,
          fontSize: 12,
          color: COLOR.muted,
          marginBottom: 14,
          lineHeight: 1.4,
        }}>
          Comma- or line-separated; each becomes its own row.
        </div>

        {/* Input — textarea so multi-line jot mode works. autosizes
            via rows attribute; tall enough to read 2-3 lines without
            scrolling but short enough to keep the qty stepper +
            ADD on screen above the keyboard. */}
        <textarea
          ref={inputRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          rows={2}
          placeholder="corn, apples, beer…"
          style={{
            width: "100%",
            padding: "14px 16px",
            background: COLOR.ground,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 14,
            color: COLOR.ink,
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: 18,
            lineHeight: 1.3,
            outline: "none",
            boxSizing: "border-box",
            resize: "none",
          }}
        />

        {/* Qty stepper — applies to every parsed item uniformly. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          margin: "16px 2px 0",
        }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
            color: COLOR.muted, letterSpacing: "0.14em",
            textTransform: "uppercase",
            flex: 1,
          }}>How many of each?</div>
          <button
            onClick={() => setQty(q => Math.max(1, q - 1))}
            style={qtyBtn}
            aria-label="Decrease"
            disabled={qty <= 1}
          >−</button>
          <div style={{
            minWidth: 36, textAlign: "center",
            fontFamily: FONT.forma, fontSize: 16, fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: COLOR.ink,
          }}>×{qty}</div>
          <button
            onClick={() => setQty(q => q + 1)}
            style={qtyBtn}
            aria-label="Increase"
          >+</button>
        </div>

        {/* Footer actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "14px",
              background: "transparent",
              border: `1px solid ${COLOR.border}`,
              color: COLOR.muted,
              borderRadius: 12,
              fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={!canAdd}
            style={{
              flex: 2,
              padding: "14px",
              // Burnt-orange CTA gradient — same family as the dock's +
              // button so primary actions read consistently across the app.
              background: canAdd
                ? "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)"
                : "rgba(217,107,43,0.18)",
              border: canAdd ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${COLOR.border}`,
              color: canAdd ? "#FFF8EE" : COLOR.muted,
              borderRadius: 12,
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: canAdd ? "pointer" : "not-allowed",
              boxShadow: canAdd
                ? "0 8px 18px rgba(168,73,17,0.30), 0 2px 4px rgba(168,73,17,0.20)"
                : "none",
              transition: "background 0.18s ease, box-shadow 0.18s ease",
            }}
          >
            {parsed.length > 1 ? `Add ${parsed.length} items` : "Add"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const qtyBtn = {
  width: 36, height: 36,
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  color: COLOR.ink,
  borderRadius: 10,
  fontFamily: FONT.forma, fontSize: 18, fontWeight: 500,
  cursor: "pointer",
  lineHeight: 1,
};
